import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Compass, Gift, Swords, TrendingUp, Users, Store, Bike, Repeat, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const SIGNAL = {
  porta_attivita: { label: "Porta attività", cls: "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10" },
  porta_amico: { label: "Porta clienti", cls: "text-[#7DF9FF] border-[#7DF9FF]/40 bg-[#7DF9FF]/10" },
  recluta_rider: { label: "Recluta rider", cls: "text-[#FFD700] border-[#FFD700]/40 bg-[#FFD700]/10" },
  equilibrio: { label: "Equilibrio", cls: "text-zinc-400 border-white/15 bg-white/5" },
};

const scoreColor = (s) => (s >= 66 ? "#00E676" : s >= 33 ? "#FFD700" : "#FF6B6B");

export default function GrowthOpportunitiesPanel() {
  const [opps, setOpps] = useState(null);
  const [cr, setCr] = useState(null);
  const [ch, setCh] = useState(null);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ title: "", comuni: "", metric: "orders", target: 10, xp_reward: 150, ends_at: "" });

  const load = () => {
    api.get("/ceo/growth/opportunities").then(({ data }) => setOpps(data)).catch(() => {});
    api.get("/ceo/growth/city-rewards").then(({ data }) => setCr(data)).catch(() => {});
    api.get("/ceo/growth/territory-challenges").then(({ data }) => setCh(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const evaluateRewards = async () => {
    try { const { data } = await api.post("/ceo/growth/city-rewards/evaluate"); toast.success(`Valutati ${data.comuni_valutati} comuni · ${data.nuovi_sblocchi.length} nuovi sblocchi`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createChallenge = async () => {
    try {
      const comuni = form.comuni.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/ceo/growth/territory-challenges", { ...form, comuni, target: Number(form.target), xp_reward: Number(form.xp_reward) });
      toast.success("Sfida creata"); setForm({ title: "", comuni: "", metric: "orders", target: 10, xp_reward: 150, ends_at: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const settle = async (id) => {
    try { await api.post(`/ceo/growth/territory-challenges/${id}/settle`); toast.success("Sfida valutata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delChallenge = async (id) => {
    try { await api.delete(`/ceo/growth/territory-challenges/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="growth-opportunities-panel">
      <div>
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><Compass className="h-5 w-5 text-[#00E676]" /> Growth Opportunity Dashboard</h2>
        <p className="text-sm text-zinc-500">Punteggio di crescita spiegabile per territorio: dove conviene espandere e cosa manca.</p>
      </div>

      {/* ===== Growth Score per comune ===== */}
      <div className="space-y-3">
        {(opps?.opportunities || []).map((o) => {
          const sig = SIGNAL[o.signal] || SIGNAL.equilibrio;
          const isOpen = open === o.comune;
          return (
            <div key={o.comune} data-testid={`opp-${o.comune}`} className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4">
              <button onClick={() => setOpen(isOpen ? null : o.comune)} className="w-full flex items-center gap-4 text-left">
                <div className="relative h-14 w-14 shrink-0 rounded-full grid place-items-center" style={{ background: `conic-gradient(${scoreColor(o.growth_score)} ${o.growth_score * 3.6}deg, rgba(255,255,255,0.08) 0)` }}>
                  <div className="h-11 w-11 rounded-full bg-[#1C1C1F] grid place-items-center font-black text-sm" style={{ color: scoreColor(o.growth_score) }}>{o.growth_score}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold">{o.comune}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${sig.cls}`}>{sig.label}</span></div>
                  <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1 flex-wrap">
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{o.active_users}</span>
                    <span className="flex items-center gap-1"><Store className="h-3 w-3" />{o.active_businesses}/{o.businesses}</span>
                    <span className="flex items-center gap-1"><Bike className="h-3 w-3" />{o.riders}</span>
                    <span className="flex items-center gap-1"><Repeat className="h-3 w-3" />{o.retention_pct}%</span>
                    <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" />conv {o.conversion}</span>
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="mt-4 pt-4 border-t border-white/10 space-y-3" data-testid={`opp-detail-${o.comune}`}>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Perché</div>
                    <ul className="text-sm text-zinc-300 list-disc pl-5 space-y-0.5">{(o.reasons || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Azioni consigliate</div>
                    <ul className="text-sm text-[#00E676] list-disc pl-5 space-y-0.5">{(o.actions || []).map((a, i) => <li key={i}>{a}</li>)}</ul>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(o.score_breakdown || {}).map(([k, v]) => (
                      <span key={k} className="text-[10px] px-2 py-1 rounded-md bg-white/5 border border-white/10 text-zinc-400">{k}: <b className="text-zinc-200">{v}</b></span>
                    ))}
                  </div>
                  {o.categories_missing?.length > 0 && <div className="text-xs text-zinc-500">Categorie mancanti: {o.categories_missing.join(", ")}</div>}
                </div>
              )}
            </div>
          );
        })}
        {opps && (opps.opportunities || []).length === 0 && <div className="text-zinc-500 text-sm py-4 text-center">Nessun dato territoriale ancora.</div>}
      </div>

      {/* ===== City Rewards (sblocchi automatici) ===== */}
      <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="text-sm font-semibold flex items-center gap-2"><Gift className="h-4 w-4 text-[#FFD700]" /> City Rewards — sblocchi automatici</div>
          <Button size="sm" variant="outline" onClick={evaluateRewards} data-testid="cr-evaluate"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Rivaluta sblocchi</Button>
        </div>
        <p className="text-xs text-zinc-500 mb-3">I benefici configurati (growth_config.city_rewards) si attivano da soli quando un comune raggiunge il livello. Config-driven, con validità temporale.</p>
        <div className="space-y-2">
          {(cr?.activations || []).map((a) => (
            <div key={a.id} data-testid={`cr-act-${a.id}`} className="flex items-center justify-between gap-3 border-t border-white/5 pt-2 text-sm flex-wrap">
              <div><span className="font-semibold">{a.comune}</span> <span className="text-zinc-500 text-xs">· Liv {a.level} · {a.benefit}{a.value ? ` (${a.value})` : ""}</span><div className="text-xs text-[#FFD700]">{a.label}</div></div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${a.expired ? "text-zinc-500 border-white/10" : "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10"}`}>{a.expired ? "Scaduto" : "Attivo"}{a.expires_at ? ` · fino ${String(a.expires_at).slice(0, 10)}` : ""}</span>
            </div>
          ))}
          {cr && (cr.activations || []).length === 0 && <div className="text-zinc-500 text-sm py-2 text-center">Nessun City Reward sbloccato. Configura i benefici in growth_config.city_rewards.</div>}
        </div>
      </div>

      {/* ===== Sfide territoriali (Città vs Città) ===== */}
      <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5">
        <div className="text-sm font-semibold flex items-center gap-2 mb-3"><Swords className="h-4 w-4 text-[#7DF9FF]" /> Sfide territoriali — Città vs Città</div>
        <div className="grid sm:grid-cols-2 gap-2 mb-3">
          <input data-testid="ch-title" placeholder="Titolo (es. Ciriè vs Nole)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0A0A0C] border border-white/10 rounded-md h-9 px-3 text-sm" />
          <input data-testid="ch-comuni" placeholder="Comuni separati da virgola" value={form.comuni} onChange={(e) => setForm({ ...form, comuni: e.target.value })} className="bg-[#0A0A0C] border border-white/10 rounded-md h-9 px-3 text-sm" />
          <select data-testid="ch-metric" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} className="bg-[#0A0A0C] border border-white/10 rounded-md h-9 px-2 text-sm"><option value="orders">Ordini</option><option value="new_users">Nuovi clienti</option><option value="gmv">GMV</option></select>
          <input data-testid="ch-target" type="number" placeholder="Obiettivo" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="bg-[#0A0A0C] border border-white/10 rounded-md h-9 px-3 text-sm" />
          <input data-testid="ch-xp" type="number" placeholder="XP ai vincitori" value={form.xp_reward} onChange={(e) => setForm({ ...form, xp_reward: e.target.value })} className="bg-[#0A0A0C] border border-white/10 rounded-md h-9 px-3 text-sm" />
          <input data-testid="ch-ends" type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="bg-[#0A0A0C] border border-white/10 rounded-md h-9 px-3 text-sm text-zinc-400" />
        </div>
        <Button size="sm" onClick={createChallenge} data-testid="ch-create" className="bg-[#00E676] text-black">Crea sfida</Button>
        <div className="space-y-3 mt-4">
          {(ch?.challenges || []).map((c) => (
            <div key={c.id} data-testid={`ch-${c.id}`} className="border-t border-white/5 pt-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-semibold">{c.title} <span className="text-zinc-500 text-xs">· {c.metric} · obiettivo {c.target}{c.settled ? " · CONCLUSA" : ""}</span></div>
                <div className="flex gap-2">
                  {!c.settled && <Button size="sm" variant="outline" onClick={() => settle(c.id)} data-testid={`ch-settle-${c.id}`}>Valuta ora</Button>}
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => delChallenge(c.id)}>Elimina</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {(c.standings || []).map((s, i) => (
                  <span key={s.comune} className={`text-xs px-2 py-1 rounded-md border ${i === 0 && s.value > 0 ? "border-[#FFD700]/50 bg-[#FFD700]/10 text-[#FFD700]" : "border-white/10 text-zinc-300"}`}>{i === 0 && s.value > 0 ? "🏆 " : ""}{s.comune}: <b>{s.value}</b></span>
                ))}
              </div>
            </div>
          ))}
          {ch && (ch.challenges || []).length === 0 && <div className="text-zinc-500 text-sm py-2 text-center">Nessuna sfida attiva.</div>}
        </div>
      </div>
    </div>
  );
}
